import {
  ElementType,
  PATTERN_SIZE_MULTIPLIERS,
  THICKNESS_PRESETS,
} from '@ritabygglov/core';
import {
  DashedBorder,
  DotdashedBorder,
  DottedBorder,
  OutlinedBorder,
  SolidBorder,
} from '../Toolbar/SvgIcons';

export interface PropertyConfig<T = string> {
  value: T;
  label?: string;
  title: string;
  icon?: string;
}

export interface StrokeDashConfig extends PropertyConfig<string | undefined> {
  additionalProps?: Record<string, unknown>;
  icon: string;
}

export function getStrokeIcon(
  iconName: string,
  isFill = false
): React.ReactNode {
  switch (iconName) {
    case 'solid':
      return <SolidBorder isFill={isFill} />;
    case 'dotted':
      return <DottedBorder isFill={isFill} />;
    case 'dashed':
      return <DashedBorder isFill={isFill} />;
    case 'dotdashed':
      return <DotdashedBorder isFill={isFill} />;
    case 'outlined':
      return <OutlinedBorder isFill={isFill} />;
    default:
      return null;
  }
}

// Thickness in canvas pixels, so a preset can be compared straight against
// element.thickness.
export const THICKNESS_PRESETS_CONFIG: PropertyConfig<number>[] = [
  { value: THICKNESS_PRESETS.S, label: 'S', title: 'controls.strokeWidthThin' },
  {
    value: THICKNESS_PRESETS.M,
    label: 'M',
    title: 'controls.strokeWidthMedium',
  },
  {
    value: THICKNESS_PRESETS.L,
    label: 'L',
    title: 'controls.strokeWidthThick',
  },
  {
    value: THICKNESS_PRESETS.XL,
    label: 'XL',
    title: 'controls.strokeWidthExtraThick',
  },
];

export const POLYLINE_THICKNESS_PRESETS_CONFIG: PropertyConfig<number>[] = [
  {
    value: THICKNESS_PRESETS.S,
    label: 'S',
    title: 'controls.polylineStrokeWidthSmall',
  },
  {
    value: THICKNESS_PRESETS.M,
    label: 'M',
    title: 'controls.polylineStrokeWidthMedium',
  },
  { value: THICKNESS_PRESETS.L, label: 'L', title: 'controls.strokeLarge' },
];

export interface FontSizeConfig {
  fontSize: string;
  fontWeight: string;
}

export const FONT_SIZES: PropertyConfig<FontSizeConfig>[] = [
  {
    value: { fontSize: '10px', fontWeight: '250' },
    label: 'S',
    title: 'controls.fontSizeSmall',
  },
  {
    value: { fontSize: '14px', fontWeight: '400' },
    label: 'M',
    title: 'controls.fontSizeMedium',
  },
  {
    value: { fontSize: '22px', fontWeight: '550' },
    label: 'L',
    title: 'controls.fontSizeLarge',
  },
  {
    value: { fontSize: '40px', fontWeight: '700' },
    label: 'XL',
    title: 'controls.fontSizeExtraLarge',
  },
];

export const PATTERN_SIZES: PropertyConfig<number>[] = [
  {
    value: PATTERN_SIZE_MULTIPLIERS.S,
    label: 'S',
    title: 'controls.patternSizeSmall',
  },
  {
    value: PATTERN_SIZE_MULTIPLIERS.M,
    label: 'M',
    title: 'controls.patternSizeMedium',
  },
  {
    value: PATTERN_SIZE_MULTIPLIERS.L,
    label: 'L',
    title: 'controls.patternSizeLarge',
  },
];

export interface DimensionSizeConfig {
  value: string;
  label: string;
  title: string;
  fontSize: string | undefined;
  showDimensions: boolean;
}

export const DIMENSION_SIZES: DimensionSizeConfig[] = [
  {
    value: 'none',
    label: 'Inga',
    title: 'controls.dimensionNone',
    fontSize: undefined,
    showDimensions: false,
  },
  {
    value: '10px',
    label: 'S',
    title: 'controls.smallDimensions',
    fontSize: '10px',
    showDimensions: true,
  },
  {
    value: '14px',
    label: 'M',
    title: 'controls.dimensionMedium',
    fontSize: '14px',
    showDimensions: true,
  },
  {
    value: '22px',
    label: 'L',
    title: 'controls.dimensionLarge',
    fontSize: '22px',
    showDimensions: true,
  },
  {
    value: '40px',
    label: 'XL',
    title: 'controls.largeDimensions',
    fontSize: '40px',
    showDimensions: true,
  },
];

export const STROKE_DASH_ARRAYS: StrokeDashConfig[] = [
  {
    value: undefined,
    title: 'controls.strokeDashSolid',
    icon: 'solid',
    additionalProps: { isOutlined: false },
  },
  {
    value: '4px',
    title: 'controls.strokeDashDotted',
    icon: 'dotted',
    additionalProps: { isOutlined: false },
  },
  {
    value: '12px',
    title: 'controls.strokeDashDashed',
    icon: 'dashed',
    additionalProps: { isOutlined: false },
  },
  {
    value: undefined,
    title: 'controls.strokeDashOutlined',
    icon: 'outlined',
    additionalProps: { isOutlined: true },
  },
];

export const POLYLINE_STROKE_DASH_ARRAYS: StrokeDashConfig[] = [
  {
    value: undefined,
    title: 'controls.strokeDashSolid',
    icon: 'solid',
    additionalProps: { isOutlined: false },
  },
  {
    value: '4px',
    title: 'controls.strokeDashDotted',
    icon: 'dotted',
    additionalProps: { isOutlined: false },
  },
  {
    value: '20 5 1 5',
    title: 'controls.strokeDashDotDashed',
    icon: 'dotdashed',
    additionalProps: { isOutlined: false },
  },
  {
    value: undefined,
    title: 'controls.strokeDashOutlined',
    icon: 'outlined',
    additionalProps: { isOutlined: true },
  },
  {
    value: '12px',
    title: 'controls.strokeDashDashedAlt',
    icon: 'dashed',
    additionalProps: { isOutlined: false },
  },
];

export type ControlGroup =
  | 'dimensions'
  | 'triangleDimensions'
  | 'trapezoidDimensions'
  | 'polylineLength'
  | 'rotation'
  | 'mirrored'
  | 'showDimensions'
  | 'stroke'
  | 'strokeColor'
  | 'pattern'
  | 'fontSize'
  | 'textAlign'
  | 'textColor'
  | 'arrows'
  | 'more'
  | 'copy'
  | 'ungroup'
  | 'delete';

export const ELEMENT_CONTROLS: Record<ElementType, ControlGroup[]> = {
  [ElementType.Rect]: [
    'dimensions',
    'rotation',
    'showDimensions',
    'pattern',
    'stroke',
    'more',
    'copy',
    'delete',
  ],
  [ElementType.Text]: ['fontSize', 'textAlign', 'textColor', 'more', 'delete'],
  [ElementType.Polyline]: [
    'arrows',
    'polylineLength',
    'showDimensions',
    'pattern',
    'stroke',
    'more',
    'copy',
    'delete',
  ],
  [ElementType.Symbol]: [
    'dimensions',
    'rotation',
    'strokeColor',
    'more',
    'copy',
    'delete',
  ],
  [ElementType.Fixture]: [
    'dimensions',
    'rotation',
    'mirrored',
    'more',
    'copy',
    'delete',
  ],
  [ElementType.Triangle]: [
    'triangleDimensions',
    'rotation',
    'showDimensions',
    'pattern',
    'stroke',
    'more',
    'copy',
    'delete',
  ],
  [ElementType.Trapezoid]: [
    'trapezoidDimensions',
    'rotation',
    'showDimensions',
    'pattern',
    'stroke',
    'more',
    'copy',
    'delete',
  ],
  [ElementType.Angle]: [
    'triangleDimensions',
    'rotation',
    'showDimensions',
    'stroke',
    'more',
    'copy',
    'delete',
  ],
  [ElementType.Group]: [
    'dimensions',
    'rotation',
    'mirrored',
    'more',
    'copy',
    'ungroup',
    'delete',
  ],
};
